'use client';

import { useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useTranslations } from 'next-intl';
import { createContext, type FC, type PropsWithChildren, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { AirdropShareModal } from '@/components/molecules/ActionCard/AirdropShareModal';
import { CelebrityShareSuccessModal } from '@/components/molecules/ActionCard/CelebrityShareSuccessModal';
import { shareAirdrop } from '@/entities/Actions/api/airdropShare';
import { notification } from '@/utils/antd-imports';
interface AirdropShareData {
  shareUrl: string;
  reward: {
    poe: number;
    coins: number;
  };
  rewardPreview: {
    label: string;
    amount: string;
  };
}
interface AirdropShareContextType {
  openAirdropShare: () => Promise<void>;
  isShareModalOpen: boolean;
  isRewardModalOpen: boolean;
  closeShareModal: () => void;
  closeRewardModal: () => void;
}
const AirdropShareContext = createContext<AirdropShareContextType | null>(null);
export const useAirdropShare = () => {
  const context = useContext(AirdropShareContext);
  if (!context) {
    throw new Error("useAirdropShare must be used within AirdropShareProvider");
  }
  return context;
};
export const AirdropShareProvider: FC<PropsWithChildren> = ({
  children
}) => {
  const tDetail = useTranslations('experiences.detail');
  const tCard = useTranslations('experiences.card');
  const queryClient = useQueryClient();
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [isRewardModalOpen, setIsRewardModalOpen] = useState(false);
  const [shareData, setShareData] = useState<AirdropShareData | null>(null);
  const [rewardModalData, setRewardModalData] = useState<{
    poe: number;
    coins: number;
  } | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [api, contextHolder] = notification.useNotification();
  const closeShareModal = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setIsShareModalOpen(false);
    setShareData(null);
  }, []);
  const closeRewardModal = useCallback(() => {
    setIsRewardModalOpen(false);
    setRewardModalData(null);
    queryClient.invalidateQueries({
      queryKey: ['user-balance']
    });
  }, [queryClient]);
  const openAirdropShare = useCallback(async () => {
    try {
      const response = await shareAirdrop();
      const amountFormatted = new Intl.NumberFormat('en-US', {
        style: 'decimal',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(response.shareContent.totalAmountUsd);
      const rewardPreview = {
        label: tCard('airdrop_last_week_reward'),
        amount: `${amountFormatted} USD`
      };
      setShareData({
        shareUrl: response.shareContent.earnLink,
        reward: response.reward,
        rewardPreview
      });
      setIsShareModalOpen(true);
      if (response.reward.poe > 0 || response.reward.coins > 0) {
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        timeoutRef.current = setTimeout(() => {
          timeoutRef.current = null;
          setIsShareModalOpen(false);
          setShareData(null);
          setRewardModalData(response.reward);
          setIsRewardModalOpen(true);
        }, 10000);
      }
    } catch (error) {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      const message = error instanceof AxiosError ? error.response?.status === 400 ? 'Airdrop share already completed this week' : error.response?.status === 401 ? 'Unauthorized' : error.response?.status === 404 ? 'No weekly reward data or airdrop share action is not configured' : undefined : undefined;
      api.error({
        message: message ?? tDetail('shareExperience.error'),
        placement: 'bottom'
      });
    }
  }, [api, tDetail, tCard]);
  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);
  return <AirdropShareContext.Provider value={{
    openAirdropShare,
    isShareModalOpen,
    isRewardModalOpen,
    closeShareModal,
    closeRewardModal
  }}>
      {contextHolder}
      {children}
      <AirdropShareModal isOpen={isShareModalOpen} onClose={closeShareModal} shareUrl={shareData?.shareUrl ?? ''} rewardPreview={shareData?.rewardPreview} />
      {rewardModalData && <CelebrityShareSuccessModal isOpen={isRewardModalOpen} onClose={closeRewardModal} reward={rewardModalData} />}
    </AirdropShareContext.Provider>;
};